<!--
  Laydown Log Form Component
  -------------------------
  This component provides the main UI for creating new laydown log entries in the Receiving Manager Web App. It offers a streamlined,
  minimalist form for users to input essential laydown log data.

  Features:
  - Responsive design: Layout adapts for small and large screens using Ionic and Angular responsive utilities.
  - Location-aware: Conditionally renders based on whether selected location supports laydown space.
    - Shows warning card with available locations when laydown not supported.
    - Shows form when laydown is supported at current location.
  - Main form fields: Storage facility (auto-filled), space selection, job number, contact (with autocomplete), date in, and estimated date out.
  - Contact autocomplete: Employee email dropdown filters as user types for quick selection.
  - Dynamic field configuration: Labels, placeholders, and required status are loaded from configuration for consistency.
  - Conditional rendering: UI elements and field requirements update based on screen size and configuration.
  - Submit button: Disabled until all required fields are valid; shows loading spinner and message during submission.

  Limitations:
  - No modals for contact management, photo upload, or additional info.
  - No print or email functionality; this form is focused solely on log entry creation.
  - No PO validation, advanced defaults, or itemized entry support.

  Notes:
  - Designed for quick, basic laydown log entry. More complex workflows are handled by other components.
  - All field and UI text is driven by configuration for maintainability and future extensibility.
-->
<div class="log-form-page-container">
  <div *ngIf="assetsDataLoaded" class="log-form-page-content">
    <!-- Show guard component if laydown not available -->
    <app-laydown-location-guard *ngIf="!selectedLocation.laydownSpaceAvailable"></app-laydown-location-guard>
    
    <!-- Only show form if laydown is available -->
    <ng-container *ngIf="selectedLocation.laydownSpaceAvailable">
      <ion-content *ngIf="isSmallScreen">
        <ng-template [ngTemplateOutlet]="appform"></ng-template>
      </ion-content>

      <ng-template
        *ngIf="!isSmallScreen"
        [ngTemplateOutlet]="appform"
      ></ng-template>
    </ng-container>
  </div>
</div>

<!--Log Form -->
<ng-template #appform>
  <form [formGroup]="logForm">
    <ion-grid class="log-form-grid">
      <!-- Row 1: Space & Storage Facility -->
      <ion-row class="log-form-row">
        <ion-col size="12" size-md="6">
          <ion-item lines="none" class="grid-ion-item">
            <ion-input
              formControlName="storageFacility"
              [label]="logFormInputs['storageFacility'].label"
              [labelPlacement]="labelPlacement"
              [disabled]="true"
              title="Change location in the App to update the storage facility"
            >
            </ion-input>
          </ion-item>
        </ion-col>

        <ion-col size="12" size-md="6">
          <ion-item lines="none" class="grid-ion-item">
            <ion-select
              formControlName="space"
              [placeholder]="
                logFormInputs['space'].placeholder ||
                'Select ' + logFormInputs['space'].label.toLowerCase()
              "
              aria-label="space"
              interface="popover"
              [labelPlacement]="labelPlacement"
              [label]="
                logFormInputs['space'].label +
                (logFormInputs['space'].required &&
                !logForm.controls['space'].value
                  ? ' *'
                  : '')
              "
            >
              <ion-select-option
                *ngFor="let option of spaceOptions"
                [value]="option.label"
              >
                {{ option.label }}
              </ion-select-option>
            </ion-select>
          </ion-item>
        </ion-col>
      </ion-row>

      <!-- Row 2: Job Number & Contact -->
      <ion-row class="log-form-row">
        <ion-col size="12" size-md="6">
          <ion-item lines="none" class="grid-ion-item">
            <app-ion-input
              [templateFormGroup]="logForm"
              [labelPlacement]="labelPlacement"
              [formField]="logFormInputs['jobNumber']"
              formControlName="jobNumber"
            >
            </app-ion-input>
          </ion-item>
        </ion-col>

        <ion-col size="12" size-md="6">
          <ion-item lines="none" class="grid-ion-item">
            <app-ion-input
              #contactInput
              [templateFormGroup]="logForm"
              [labelPlacement]="labelPlacement"
              [formField]="logFormInputs['contact']"
              formControlName="contact"
              [disabledCondition]="isLoadingContacts"
            >
            </app-ion-input>
            <ion-spinner 
              *ngIf="isLoadingContacts" 
              name="crescent" 
              slot="end"
              style="margin-left: 8px;"
            ></ion-spinner>
          </ion-item>
          
          <!-- Loading message -->
          <ion-text 
            *ngIf="isLoadingContacts" 
            color="medium" 
            style="font-size: 0.875rem; margin-left: 16px; display: block; margin-top: 4px;"
          >
            Loading employee contacts...
          </ion-text>
          
          <!-- Validation error message -->
          <ion-text 
            *ngIf="!isLoadingContacts && logForm.get('contact')?.hasError('notSelected') && logForm.get('contact')?.touched" 
            color="danger" 
            style="font-size: 0.875rem; margin-left: 16px; display: block; margin-top: 4px;"
          >
            Must select valid company email
          </ion-text>
          
          <!-- Autocomplete dropdown positioned outside ion-item -->
          <app-autocomplete-dropdown
            *ngIf="!isLoadingContacts"
            [options]="employeeEmails"
            [formGroup]="logForm"
            controlName="contact"
            [inputElement]="contactInput?.inputElementRef?.nativeElement"
          >
          </app-autocomplete-dropdown>
        </ion-col>
      </ion-row>

      <!-- Row 3: Date In & Est Date Out -->
      <ion-row class="log-form-row">
        <ion-col size="12" size-md="6">
          <ion-item lines="none" class="grid-ion-item">
            <div class="datetime-wrapper">
              <ion-label class="datetime-label">
                {{ logFormInputs["dateIn"].label
                }}{{
                  logFormInputs["dateIn"].required &&
                  !logForm.controls["dateIn"].value
                    ? "*"
                    : ""
                }}
              </ion-label>
              <ion-datetime
                presentation="date"
                formControlName="dateIn"
                class="compact-datetime"
              >
              </ion-datetime>
            </div>
          </ion-item>
        </ion-col>

        <ion-col size="12" size-md="6">
          <ion-item lines="none" class="grid-ion-item">
            <div class="datetime-wrapper">
              <ion-label class="datetime-label">
                {{ logFormInputs["estDateOut"].label
                }}{{
                  logFormInputs["estDateOut"].required &&
                  !logForm.controls["estDateOut"].value
                    ? "*"
                    : ""
                }}
              </ion-label>
              <ion-datetime
                presentation="date"
                formControlName="estDateOut"
                class="compact-datetime"
              >
              </ion-datetime>
            </div>
          </ion-item>
        </ion-col>
      </ion-row>

      <!-- Submit Button -->
           <div class="submit-button-row">
          <ion-button
            expand="full"
            color="lightBlue"
            class="submit-btn"
            [disabled]="!logForm.valid"
            (click)="onSubmitLog()"
          >
            <ion-icon
              *ngIf="!dataLoading.isLoading"
              name="checkmark"
            ></ion-icon>

            <ion-spinner
              *ngIf="dataLoading.isLoading"
              name="crescent"
            ></ion-spinner>

            &nbsp;{{
              dataLoading.isLoading ? dataLoading.message : submitButtonText
            }}
          </ion-button>
        </div>
    </ion-grid>
  </form>
</ng-template>
